import React, { useState } from "react";
import pricing from "@content/pricing.json";
import batches from "@content/batches.json";
import settings from "@content/settings.json";

const Pricing = () => {
    const [session, setSession] = useState("evening");

    return (
        <>
            {/* Risk Free */}
            <section className="section" id="risk-free" data-testid="risk-section">
                <div className="container-p">
                    <div className="panel p-5 md:p-8 relative overflow-hidden">
                        <div
                            className="absolute -top-16 -right-16 w-72 h-72 rounded-full pointer-events-none"
                            style={{
                                background: "radial-gradient(circle, rgba(124,231,59,0.2), transparent 65%)",
                            }}
                            aria-hidden
                        />
                        <div className="relative grid md:grid-cols-[1fr_auto] items-center gap-6">
                            <div>
                                <span className="eyebrow">Zero Financial Risk</span>
                                <h2 className="mt-4 font-display text-3xl md:text-5xl font-extrabold text-slate-900">
                                    Try It <span className="text-brand">Risk-Free.</span>
                                </h2>
                                <p className="mt-4 text-dim text-base md:text-lg max-w-2xl">
                                    {pricing.money_back_line}
                                    <br />
                                    <span className="text-slate-900">No questions. No forms. No stress.</span>
                                </p>
                                <div className="mt-6 flex flex-wrap gap-2">
                                    {pricing.risk_bullets.map((b) => (
                                        <span
                                            key={b}
                                            className="font-mono text-xs px-3 py-1.5 rounded-md border border-[rgba(124,231,59,0.35)] bg-[rgba(124,231,59,0.06)] text-brand tracking-wider uppercase"
                                        >
                                            {b}
                                        </span>
                                    ))}
                                </div>
                            </div>
                            <a
                                href={settings.razorpay_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn-primary text-base md:text-lg"
                                data-testid="risk-cta"
                            >
                                Claim My Seat - ₹799 →
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* Pricing */}
            <section className="section" id="pricing" data-testid="pricing-section">
                <div className="container-p">
                    <div className="max-w-3xl">
                        <span className="eyebrow">Reserve Your Seat</span>
                        <h2 className="mt-4 font-display text-3xl md:text-5xl font-extrabold leading-tight text-slate-900">
                            Your New Career Starts for{" "}
                            <span className="text-brand">{pricing.offer_price}.</span>
                        </h2>
                        <p className="mt-4 text-dim text-base md:text-lg">
                            The most valuable {pricing.offer_price} you'll invest in your IT career — for
                            the skills that can change your salary and your future. Pick your batch and
                            lock your seat.
                        </p>
                    </div>

                    <div className="mt-6 grid lg:grid-cols-2 gap-4 items-stretch">
                        {/* What's included */}
                        <div className="panel p-5 md:p-6 flex flex-col h-full">
                            <div className="font-mono text-xs uppercase tracking-widest text-brand">
                                What's Included in Your Access
                            </div>
                            <h3 className="mt-3 font-display text-2xl font-bold text-slate-900">
                                Full Grads2Pro Live Access
                            </h3>
                            <ul className="mt-6 space-y-3.5">
                                {pricing.included.map((it) => (
                                    <li key={it} className="flex items-start gap-3 text-slate-900">
                                        <span className="mt-1 w-5 h-5 shrink-0 rounded-full bg-[rgba(124,231,59,0.15)] border border-[rgba(124,231,59,0.4)] text-brand flex items-center justify-center">
                                            <svg width="12" height="12" viewBox="0 0 24 24" aria-hidden>
                                                <path fill="currentColor" d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                                            </svg>
                                        </span>
                                        <span className="text-[15px] md:text-base leading-relaxed">
                                            {it}
                                        </span>
                                    </li>
                                ))}
                            </ul>

                            <div className="mt-auto pt-4 space-y-3">
                                <div className="rounded-lg border border-[rgba(124,231,59,0.3)] bg-[rgba(124,231,59,0.06)] p-4 text-sm text-dim leading-relaxed">
                                    Backed by our{" "}
                                    <span className="text-slate-900 font-semibold">
                                        100% Money-Back Guarantee.
                                    </span>{" "}
                                    Attend live, evaluate the hands-on labs, or ask for an instant 100% refund.
                                </div>
                                <div className="flex items-center gap-3 text-sm text-dim">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="text-brand shrink-0">
                                        <path d="M12 2l7 4v6c0 5-3.5 8.5-7 10-3.5-1.5-7-5-7-10V6l7-4z" />
                                        <path d="M9 12l2 2 4-4" />
                                    </svg>
                                    <span>Secure Razorpay checkout · UPI · Cards · Wallets</span>
                                </div>
                                <div className="flex items-center gap-3 text-sm text-dim">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="text-brand shrink-0">
                                        <rect x="3" y="4" width="18" height="16" rx="2" />
                                        <path d="M3 10h18M8 4v4M16 4v4" />
                                    </svg>
                                    <span>New batch starts <span className="text-slate-900 font-semibold">every Monday</span></span>
                                </div>
                            </div>
                        </div>

                        {/* Purchase card */}
                        <div className="panel p-5 md:p-6 relative overflow-hidden flex flex-col h-full">
                            <div
                                className="absolute inset-0 pointer-events-none"
                                style={{
                                    background:
                                        "radial-gradient(500px 240px at 100% 0%, rgba(124,231,59,0.08), transparent 60%)",
                                }}
                                aria-hidden
                            />
                            <div className="relative space-y-5 flex-1 flex flex-col">
                                <div>
                                    <div className="font-mono text-xs uppercase tracking-widest text-mute">
                                        Select Session Schedule
                                    </div>
                                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <button
                                            onClick={() => setSession("morning")}
                                            className={`text-left rounded-xl border p-5 transition-all ${
                                                session === "morning"
                                                    ? "border-[rgba(124,231,59,0.65)] bg-[rgba(124,231,59,0.08)] shadow-[0_4px_16px_-8px_rgba(124,231,59,0.35)]"
                                                    : "border-[var(--border)] bg-slate-50 hover:border-[var(--text-mute)]"
                                            }`}
                                            data-testid="session-morning"
                                        >
                                            <div className="font-mono text-[11px] text-brand uppercase tracking-widest">
                                                Morning Session
                                            </div>
                                            <div className="mt-2 font-display font-bold text-slate-900 text-2xl leading-none">
                                                {batches.batch_time_morning}
                                            </div>
                                            <div className="mt-2 text-xs text-dim leading-snug">
                                                {batches.batch_time_morning_note}
                                            </div>
                                        </button>
                                        <button
                                            onClick={() => setSession("evening")}
                                            className={`text-left rounded-xl border p-5 transition-all ${
                                                session === "evening"
                                                    ? "border-[rgba(124,231,59,0.65)] bg-[rgba(124,231,59,0.08)] shadow-[0_4px_16px_-8px_rgba(124,231,59,0.35)]"
                                                    : "border-[var(--border)] bg-slate-50 hover:border-[var(--text-mute)]"
                                            }`}
                                            data-testid="session-evening"
                                        >
                                            <div className="font-mono text-[11px] text-brand uppercase tracking-widest">
                                                Evening Session
                                            </div>
                                            <div className="mt-2 font-display font-bold text-slate-900 text-2xl leading-none">
                                                {batches.batch_time_evening}
                                            </div>
                                            <div className="mt-2 text-xs text-dim leading-snug">
                                                {batches.batch_time_evening_note}
                                            </div>
                                        </button>
                                    </div>
                                </div>

                                <div className="rounded-xl border border-[var(--border)] bg-slate-50 p-5">
                                    <div className="flex items-center justify-between gap-4">
                                        <div>
                                            <div className="font-mono text-[11px] uppercase tracking-widest text-mute">
                                                Next Session Start
                                            </div>
                                            <div className="mt-1.5 font-display font-bold text-slate-900 text-lg leading-tight">
                                                {batches.batch_date}
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="font-mono text-[11px] text-brand uppercase tracking-widest">
                                                {batches.remaining_seats} Seats Left
                                            </div>
                                            <div className="mt-1.5 text-xs text-dim">
                                                {batches.filled_percentage}% filled
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mt-4 h-2 rounded-full bg-slate-200 overflow-hidden">
                                        <div
                                            className="h-full"
                                            style={{
                                                width: `${batches.filled_percentage}%`,
                                                background:
                                                    "linear-gradient(90deg, #7ce73b, #5cc823)",
                                            }}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <div className="flex items-end gap-4">
                                        <div className="font-mono text-lg line-through text-mute leading-none pb-2">
                                            {pricing.actual_price}
                                        </div>
                                        <div className="font-display text-5xl md:text-6xl font-extrabold text-slate-900 leading-none tracking-tight">
                                            {pricing.offer_price}
                                        </div>
                                        <div className="pb-2 font-mono text-xs px-2 py-1 rounded bg-[rgba(124,231,59,0.15)] text-brand border border-[rgba(124,231,59,0.35)]">
                                            {pricing.discount}
                                        </div>
                                    </div>
                                    <div className="mt-3 text-xs text-brand font-mono">
                                        🔥 {pricing.offer_end}
                                    </div>
                                </div>

                                <a
                                    href={settings.razorpay_link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn-primary w-full mt-auto text-lg py-4"
                                    data-testid="pricing-cta"
                                >
                                    Reserve My Seat →
                                </a>
                                <div className="text-center text-[11px] font-mono text-mute tracking-widest uppercase">
                                    Secure 256-bit encrypted SSL checkout
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default Pricing;
